import React, { useContext } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Select,
    MenuItem,
    Switch,
    Tooltip,
    SelectChangeEvent,
    Box,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    TextField,
    useTheme
} from '@mui/material';
import { ThemeContext } from '../../../stores/ThemeContext';
import { useGeneralInfoStore } from '../../../stores/GeneralInfoStore';
import { useCommentOverlayStore } from '../../../stores/CommentStore';
import { useTranslation } from 'react-i18next';
import { DoNotDisturbAlt, Share } from '@mui/icons-material';
import Sun from '../../../assets/icons/sun';
import Moon from '../../../assets/icons/moon';
import Communication from '../../../assets/icons/demoIcons/communication'; // Kommentar-Icon
import OrganizationLogo2 from './OrganizationLogo2';
import { sharedState, translation, versionNumber } from '../../../config/config';
import appRoutesConfig from '../../../config/appConfig';

interface SettingsDialogProps {
    open: boolean;
    onClose: () => void;

    // Theme-Farben
    checkedColor: string;
    trackColor: string;
    iconColor: string;
    textColor: string;
}

export default function SettingsDialog({
    open,
    onClose,
    checkedColor,
    trackColor,
    iconColor,
    textColor,
}: SettingsDialogProps) {

    // Darkmode / Shared
    const { darkMode, toggleDarkMode } = useContext(ThemeContext);
    const { sharedVariable, toggleSharedVariable } = useGeneralInfoStore();
    const theme = useTheme();
    // Comments
    const showCommentSection = appRoutesConfig.Options.CommentSection;
    const isOverlayOpen = useCommentOverlayStore(state => state.isOverlayOpen);
    const toggleCommentsOverlay = () => {
        useCommentOverlayStore.getState().setIsOverlayOpen(true);
    };

    // i18n
    const { t, i18n } = useTranslation();

    // Handlers
    const handleToggleDarkMode = () => {
        toggleDarkMode();
    };
    const handleToggleShared = () => {
        toggleSharedVariable();
    };
    const handleChangeLanguage = (event: SelectChangeEvent) => {
        i18n.changeLanguage(event.target.value as string);
    };
    const handleCommentsClick = () => {
        toggleCommentsOverlay();
        // Falls Dialog schließen gewünscht:
        onClose();
    };

    // Dummy-Feld für Notification-Level-Dropdown
    const [notificationLevel, setNotificationLevel] = React.useState('all');
    const handleNotificationChange = (event: SelectChangeEvent) => {
        setNotificationLevel(event.target.value as string);
    };

    // Dummy-Handler für "Change Password"
    const handleChangePassword = () => {
        alert(" Hier könnte ein 'Passwort ändern'-Dialog aufgehen!");
    };

    // Dummy-Handler für "Update Nickname"
    const handleUpdateNickname = () => {
        alert(" Nickname gespeichert!");
    };

    // Nickname im TextField
    const [nickname, setNickname] = React.useState('');
    const handleNicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNickname(e.target.value);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            // 80% Screen-Breite/Höhe
            PaperProps={{
                sx: {
                    opacity: .95,
                    width: '80vw',
                    height: '80vh',
                    maxWidth: 'none',
                    backgroundColor: theme.palette.customText.logoInvert,
                    boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.2)',
                }
            }}
        >
            <DialogTitle sx={{ color: textColor }}>
                {t('settings.title') || 'Settings'}
            </DialogTitle>

            <DialogContent dividers sx={{ padding: '1.5rem', position: 'relative' }}>

                {/* 
                  Haupt-Container mit 2 Reihen:
                  - Erste Reihe: Zwei Spalten (links/rechts, je 6) 
                  - Zweite Reihe: Volle Breite (xs=12) -> User Settings
                */}
                <Grid container spacing={3}>

                    {/* LINKE GRUPPE (xs=6): Anzeige-Einstellungen */}
                    <Grid item xs={6}>
                        <Box
                            sx={{
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                p: 2
                            }}
                        >
                            <Typography variant="h2" sx={{ color: textColor, mb: 2 }}>
                                {t('settings.displayGroup') || 'Anzeige-Einstellungen'}
                            </Typography>

                            {/* Darkmode + Shared UNTEREINANDER */}
                            {/* Darkmode */}
                            <Box mb={3}>
                                <Typography variant="h3" sx={{ color: textColor, mb: 1 }}>
                                    {t('settings.darkmodeTitle') || 'Dark Mode'}
                                </Typography>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <Typography variant="h4" sx={{ color: textColor }}>
                                        {t('settings.darkmodeDescription') || 'An / Aus'}
                                    </Typography>
                                    <Switch
                                        checked={darkMode === 'light'}
                                        onChange={handleToggleDarkMode}
                                        sx={{
                                            width: '66px',
                                            borderRadius: '30px',
                                            '& .MuiSwitch-switchBase': {
                                                margin: '6px',
                                                padding: '6px',
                                                '&.Mui-checked': {
                                                    color: checkedColor,
                                                    '& + .MuiSwitch-track': {
                                                        backgroundColor: trackColor,
                                                        opacity: 1,
                                                    },
                                                },
                                            },
                                            '& .MuiSwitch-thumb': {
                                                width: '20px',
                                                height: '20px',
                                            },
                                            '& .MuiSwitch-track': {
                                                boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.2)',
                                                backgroundColor: trackColor,
                                                width: '120px',
                                                height: '20px',
                                                borderRadius: '30px',
                                                opacity: 1,
                                            },
                                        }}
                                        checkedIcon={<Sun color={iconColor} size={18} />}
                                        icon={<Moon color={iconColor} size={20} />}
                                    />
                                </Box>
                            </Box>

                            {/* Shared */}
                            {sharedState && (
                                <Box>
                                    <Typography variant="h3" sx={{ color: textColor, mb: 1 }}>
                                        {t('settings.sharedTitle') || 'Geteilte Standorte'}
                                    </Typography>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <Typography variant="h4" sx={{ color: textColor }}>
                                            {t('settings.sharedDescription') || 'Gemeinsam nutzen'}
                                        </Typography>
                                        <Tooltip title="Dieser Schalter aktiviert, dass alle Standorte in allen Diagrammen mit der Karte geteilt werden.">
                                            <Switch
                                                checked={sharedVariable}
                                                onChange={handleToggleShared}
                                                sx={{
                                                    width: '66px',
                                                    borderRadius: '30px',
                                                    '& .MuiSwitch-switchBase': {
                                                        margin: '6px',
                                                        padding: '6px',
                                                        '&.Mui-checked': {
                                                            color: checkedColor,
                                                            '& + .MuiSwitch-track': {
                                                                backgroundColor: trackColor,
                                                                opacity: 1,
                                                            },
                                                        },
                                                    },
                                                    '& .MuiSwitch-thumb': {
                                                        width: '20px',
                                                        height: '20px',
                                                    },
                                                    '& .MuiSwitch-track': {
                                                        boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.2)',
                                                        backgroundColor: trackColor,
                                                        width: '120px',
                                                        height: '20px',
                                                        borderRadius: '30px',
                                                        opacity: 1,
                                                    },
                                                }}
                                                checkedIcon={<Share sx={{ fontSize: '16px', margin: '3px', color: iconColor }} />}
                                                icon={<DoNotDisturbAlt sx={{ fontSize: '16px', margin: '3px', color: iconColor }} />}
                                            />
                                        </Tooltip>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    {/* RECHTE GRUPPE (xs=6): Weitere Einstellungen */}
                    <Grid item xs={6}>
                        <Box
                            sx={{
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                p: 2
                            }}
                        >
                            <Typography variant="h2" sx={{ color: textColor, mb: 2 }}>
                                {t('settings.otherGroup') || 'Weitere Einstellungen'}
                            </Typography>

                            {/* Sprache */}
                            {translation && (
                                <Box mb={3}>
                                    <Typography variant="h3" sx={{ color: textColor, mb: 1 }}>
                                        {t('settings.languageTitle') || 'Sprache'}
                                    </Typography>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <Typography variant="h4" sx={{ color: textColor }}>
                                            {t('settings.languageDescription') || 'Bitte wählen'}
                                        </Typography>
                                        <Select
                                            value={i18n.language}
                                            onChange={handleChangeLanguage}
                                            size="small"
                                            sx={{ minWidth: '80px', color: textColor }}
                                        >
                                            <MenuItem value="de">
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <img
                                                        src="https://flagcdn.com/w20/de.png"
                                                        alt="Germany"
                                                        width="20"
                                                        height="15"
                                                    />
                                                    {t('languages.german') || 'Deutsch'}
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="en">
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <img
                                                        src="https://flagcdn.com/w20/gb.png"
                                                        alt="United Kingdom"
                                                        width="20"
                                                        height="15"
                                                    />
                                                    {t('languages.english') || 'English'}
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="es">
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <img
                                                        src="https://flagcdn.com/w20/es.png"
                                                        alt="Spain"
                                                        width="20"
                                                        height="15"
                                                    />
                                                    {t('languages.spanish') || 'Español'}
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="fr">
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <img
                                                        src="https://flagcdn.com/w20/fr.png"
                                                        alt="France"
                                                        width="20"
                                                        height="15"
                                                    />
                                                    {t('languages.french') || 'Français'}
                                                </Box>
                                            </MenuItem>
                                        </Select>
                                    </Box>
                                </Box>
                            )}
                            {/* Kommentare */}
                            {showCommentSection && (
                                <Box>
                                    <Typography variant="h3" sx={{ color: textColor, mb: 1 }}>
                                        {t('header.comments') || 'Kommentare'}
                                    </Typography>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <Typography variant="h4" sx={{ color: textColor }}>
                                            {t('settings.commentsDescription') || 'Öffnet das Kommentar-Fenster'}
                                        </Typography>
                                        <Tooltip title="Öffnet das Kommentar-Overlay">
                                            <IconButton
                                                onClick={handleCommentsClick}
                                                sx={{ color: iconColor }}
                                            >
                                                <Communication size={16} color={iconColor} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    {/* DRITTE GRUPPE (xs=12, volle Breite): MOCK “User Settings” 
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                p: 2
                            }}
                        >
                            <Typography variant="h2" sx={{ color: textColor, mb: 2 }}>
                                {t('settings.userSettingsGroup') || 'User Settings'}
                            </Typography>

                            <Typography variant="h4" sx={{ color: textColor, mb: 1 }}>
                                Hier ein Dropdown für “Notification Level”
                            </Typography>
                            <Box mb={3} display="flex" alignItems="center" gap={2}>
                                <Select
                                    value={notificationLevel}
                                    onChange={handleNotificationChange}
                                    size="small"
                                    sx={{ color: textColor }}
                                >
                                    <MenuItem value="all">All Notifications</MenuItem>
                                    <MenuItem value="mentions">Only Mentions</MenuItem>
                                    <MenuItem value="none">No Notifications</MenuItem>
                                </Select>
                                <Button variant="outlined" onClick={() => alert("Notifications saved!")}>
                                    Save
                                </Button>
                            </Box>

                            <Typography variant="h4" sx={{ color: textColor, mb: 1 }}>
                                “Change Password”
                            </Typography>
                            <Box mb={3}>
                                <Button variant="contained" onClick={handleChangePassword}>
                                    Change Password
                                </Button>
                            </Box>

                            <Typography variant="h4" sx={{ color: textColor, mb: 1 }}>
                                “Nickname”
                            </Typography>
                            <Box display="flex" alignItems="center" gap={2}>
                                <TextField
                                    label="Username"
                                    variant="outlined"
                                    size="small"
                                    value={nickname}
                                    onChange={handleNicknameChange}
                                    sx={{
                                        color: textColor,
                                        // Falls du den Label/Text anpassen willst, z. B.:
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: '#ccc',
                                            },
                                        }
                                    }}
                                />
                                <Button variant="contained" onClick={handleUpdateNickname}>
                                    Update
                                </Button>
                            </Box>
                        </Box>
                    </Grid>*/}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: '0rem',
                            right: '2rem',
                            width: '250px',
                            height: '250px',
                        }}
                    >
                        <OrganizationLogo2 />

                    </Box>
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: '1rem',
                            left: '1rem',
                        }}
                    >
                        <Typography variant="h1" fontSize={".75rem"} sx={{ color: textColor, mt: 3, mr: 3 }}>
                            {t('settings.versionNumber')} {versionNumber}
                        </Typography>
                    </Box>
                </Grid>

            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    {t('common.buttons.close') || 'Schließen'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
